'use client';
import { useState, useRef } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import Link from 'next/link';
import { ArrowLeft, Copy, Download, Check, FileUp } from 'lucide-react';

export default function CsvToJsonPage() {
  const [csvInput, setCsvInput] = useState('');
  const [jsonOutput, setJsonOutput] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const convertCsvToJson = () => {
    try {
      setError('');
      if (!csvInput.trim()) {
        setError('Please enter CSV data');
        return;
      }

      const lines = csvInput.trim().split('\n');
      if (lines.length < 2) {
        setError('CSV must have at least a header row and one data row');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim());
      const result = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const obj: any = {};
        headers.forEach((header, index) => {
          obj[header] = values[index] || '';
        });
        result.push(obj);
      }

      setJsonOutput(JSON.stringify(result, null, 2));
    } catch (err) {
      setError('Error converting CSV to JSON. Please check your input format.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonOutput);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadJson = () => {
    const blob = new Blob([jsonOutput], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'converted.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (file: File) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      setError('Please upload a CSV or TXT file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvInput(text);
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                CSV to JSON Converter Online
              </h1>
            <p className="text-lg text-muted-foreground">
              Convert your CSV data to JSON format instantly
            </p>
          </div>

          {/* File Upload Area - Full Width */}
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">Upload CSV File</label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
              }`}
            >
              <FileUp className={`w-12 h-12 mx-auto mb-4 ${
                isDragging ? 'text-blue-500' : 'text-gray-400'
              }`} />
              <p className="text-lg font-medium mb-2">
                {isDragging ? 'Drop your file here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-sm text-muted-foreground">
                Supports .csv and .txt files
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">CSV Input</label>
                <Textarea
                  placeholder="name,age,city&#10;John,30,New York&#10;Jane,25,London&#10;Bob,35,Paris"
                  value={csvInput}
                  onChange={(e) => setCsvInput(e.target.value)}
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>
              <Button 
                onClick={convertCsvToJson} 
                className="w-full"
                size="lg"
              >
                Convert to JSON
              </Button>
              {error && (
                <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  {error}
                </div>
              )}
            </div>

            {/* Output Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">JSON Output</label>
                <Textarea
                  value={jsonOutput}
                  readOnly
                  placeholder="Your JSON output will appear here..."
                  className="min-h-[200px] font-mono text-sm bg-muted"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={copyToClipboard} 
                  className="flex-1"
                  variant="outline"
                  disabled={!jsonOutput}
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button 
                  onClick={downloadJson} 
                  className="flex-1"
                  variant="outline"
                  disabled={!jsonOutput}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </div>

          {/* Example */}
          <div className="mt-8 p-6 bg-card rounded-lg border">
            <h3 className="font-semibold mb-2">Example CSV:</h3>
            <pre className="text-sm bg-muted p-4 rounded overflow-x-auto">
              name,age,city{'\n'}
              John,30,New York{'\n'}
              Jane,25,London{'\n'}
              Bob,35,Paris
            </pre>
          </div>
        </div>
      </main>

      {/* User Problems & FAQs for SEO */}
        <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold mb-4">Common CSV to JSON Questions & Problems</h2>
          <div className="space-y-6">
            <div><p className="font-semibold">1. How do I convert CSV to JSON online?</p><p className="text-muted-foreground">Paste or upload your CSV data above and click convert. The JSON will be generated instantly.</p></div>
            <div><p className="font-semibold">2. What CSV format is supported?</p><p className="text-muted-foreground">The tool expects a header row followed by data rows, separated by commas.</p></div>
            <div><p className="font-semibold">3. Can I download the JSON result?</p><p className="text-muted-foreground">Yes, after conversion, you can copy or download the JSON output.</p></div>
            <div><p className="font-semibold">4. Is my data secure?</p><p className="text-muted-foreground">All conversion happens in your browser. No data is uploaded to any server.</p></div>
            <div><p className="font-semibold">5. What if my CSV is invalid?</p><p className="text-muted-foreground">The tool will show an error if your CSV is not valid or missing headers.</p></div>
            <div><p className="font-semibold">6. Can I convert large CSV files?</p><p className="text-muted-foreground">For very large files, browser performance may vary. For huge files, use a desktop tool.</p></div>
            <div><p className="font-semibold">7. Can I use this tool for free?</p><p className="text-muted-foreground">Yes, this online CSV to JSON converter is free to use.</p></div>
            <div><p className="font-semibold">8. Does it support special characters?</p><p className="text-muted-foreground">Yes, as long as your CSV is properly formatted and quoted.</p></div>
            <div><p className="font-semibold">9. Can I copy the JSON output?</p><p className="text-muted-foreground">Yes, use the Copy button to copy the JSON result to your clipboard.</p></div>
            <div><p className="font-semibold">10. What browsers are supported?</p><p className="text-muted-foreground">All modern browsers are supported, including Chrome, Firefox, and Edge.</p></div>
          </div>
        </section>
      <div className="text-center text-xs text-muted-foreground pb-4">online</div>
      <Footer />
    </div>
  );
}
